import { type ReactElement, type ReactNode } from 'react';
import { type HealthState } from '@/hooks/use-health';
import { BrandMark } from '@/components/brand/brand-mark';
import { LanguageToggle } from '@/components/language-toggle';
import { cn } from '@/lib/utils';

const TONE = {
  checking: 'bg-muted text-muted-foreground',
  ok: 'bg-emerald-100 text-emerald-800',
  degraded: 'bg-amber-100 text-amber-900',
  offline: 'bg-red-100 text-red-800',
} as const;

// Header stays on one row at every width; the label drops before it wraps.
export function AppHeader({
  health,
  children,
}: {
  health: HealthState;
  children?: ReactNode;
}): ReactElement {
  const tone = health.phase === 'reachable' ? health.report.status : health.phase;
  return (
    <header className="sticky top-0 z-40 shrink-0 border-b bg-background/90 px-safe pt-safe backdrop-blur">
      {/* The row keeps its own height; the padding above it is the notch. §20 */}
      <div className="flex h-14 items-center gap-2 px-3 sm:gap-3 sm:px-4">
        <span className="grid size-8 shrink-0 place-items-center rounded-[10px] bg-linear-to-br from-accent to-primary text-primary-foreground sm:size-9">
          <BrandMark className="size-6 sm:size-7" />
        </span>
        <h1 className="hidden truncate text-base font-bold tracking-tight sm:block">KratiSpot</h1>
        <div className="ml-auto flex min-w-0 items-center gap-1.5 sm:gap-2">
          <LanguageToggle />
          <span
            data-testid="api-health"
            className={cn(
              'hidden shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold sm:inline',
              TONE[tone],
            )}
          >
            {labelFor(health)}
          </span>
          {children}
        </div>
      </div>
    </header>
  );
}

function labelFor(health: HealthState): string {
  if (health.phase === 'checking') return '…';
  if (health.phase === 'offline') return 'API offline';
  const { status, version, database } = health.report;
  return status === 'ok' ? `API ${version}` : `API degraded · db ${database}`;
}
